import type { i18n as I18nInstance } from 'i18next';

const normalize = (value: string) => value.replace(/[^a-z0-9]/gi, '').toLowerCase();

const humanize = (value: string) =>
  value
    .replace(/\./g, ' ')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Grid labels are supplied by the API and persisted per company, so their
 * casing can differ from the keys in the locale files. Resolve them against
 * the entity dictionary without exposing `entity.SomeLabel` to the UI.
 */
export const translateEntityLabel = (i18n: I18nInstance, label?: string | null, entity?: string | null): string => {
  const rawLabel = String(label ?? '').trim();
  const rawEntity = String(entity ?? '').trim();
  const candidates = [rawLabel, rawEntity, rawEntity.split('.').pop() ?? ''].filter(Boolean);

  for (const candidate of candidates) {
    const key = `entity.${candidate}`;
    if (i18n.exists(key)) return String(i18n.t(key));
  }

  const bundle = i18n.getResourceBundle(i18n.resolvedLanguage ?? i18n.language, 'translation');
  const entityTranslations = bundle?.entity as Record<string, unknown> | undefined;

  if (entityTranslations) {
    for (const candidate of candidates) {
      const normalizedCandidate = normalize(candidate);
      const matchingKey = Object.keys(entityTranslations).find((key) => normalize(key) === normalizedCandidate);
      if (matchingKey && typeof entityTranslations[matchingKey] === 'string') {
        return entityTranslations[matchingKey] as string;
      }
    }
  }

  return humanize(rawLabel || rawEntity);
};
